import * as z from 'zod'
import { oneKeyRecord, TOneKeyRecord } from '@obvious.tech/util/record'
import { TSNoop } from '@obvious.tech/util/validation/schemas'
import { TRequiredKeys } from '../types'

type TEntityName = string
type TEntityEndpoint = string
type TEntitySchema = z.ZodTypeAny

export const defineEntity = <
  Name extends TEntityName,
  Endpoint extends TEntityEndpoint,
  Schema extends TEntitySchema,
>(
  name: Name,
  opts: Omit<TEntityEntry<Name, Endpoint, Schema>, '__types' | 'name'>,
): TEntityDef<Name, Endpoint, Schema> => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return oneKeyRecord(name, {
    ...opts,
    name,
  })
}

export type TEntityDefs = Record<
  TEntityName,
  TEntityEntry<TEntityName, TEntityEndpoint, TEntitySchema>
>

type TEntityFromSchema<Schema extends TEntitySchema> = Schema extends TSNoop<
  infer UnsafeType
>
  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
    UnsafeType extends Record<string, any>
    ? TDeepUnwrapConstellationClass<TRequiredKeys<UnsafeType, 'id' | 'groups'>>
    : never
  : z.infer<Schema>

type TEntityEntry<
  Name extends TEntityName,
  Endpoint extends TEntityEndpoint,
  EntitySchema extends TEntitySchema,
> = {
  name: Name
  endpoint: Endpoint
  schema: EntitySchema

  /** @internal carrier for type-level lookup (erased at runtime) */
  __types?: {
    entity: TEntityFromSchema<EntitySchema>
  }
}

type TEntityDef<
  Name extends TEntityName = TEntityName,
  Endpoint extends TEntityEndpoint = TEntityEndpoint,
  Schema extends TEntitySchema = TEntitySchema,
> = TOneKeyRecord<Name, TEntityEntry<Name, Endpoint, Schema>>

type TPrimitive = string | number | boolean | null | undefined

type TDeepUnwrapConstellationClass<T> = T extends TPrimitive
  ? T
  : T extends Date
  ? string
  : T extends object
  ? TDeepFromInstance<T> extends never
    ? { [K in keyof T]: TDeepUnwrapConstellationClass<T[K]> }
    : TDeepUnwrapConstellationClass<
        Omit<TDeepFromInstance<T>, 'init' | 'toJSON'>
      >
  : T

type TDeepFromInstance<I> = I extends {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  init: (data?: any) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toJSON: (data?: any) => any
}
  ? Omit<I, 'toJSON' | 'init'>
  : never
